import { store } from '@/plugins/store';
import { EnergyUnitId } from '@/types';

/**
 * Resolves the Energy column's heading text for the per-100kg nutrient figures Feed Mixes, Raw
 * Materials, and Approved Feed Deliveries all carry: the site's configured display text when one is
 * set, else the static FU/MJ label implied by the directory's `settings.energy.id`. Shared so every
 * table reads the same figure identically rather than drifting; each caller supplies its own i18n
 * lookup since each table uses its own translation key namespace.
 *
 * Compares against `settings.energy.id` (the stable `dbo.EnergyUnit` id), never the top-level
 * `energy_unit` field - FU and MJ NE are genuinely different feed-energy systems, not two names for
 * the same number, and `energy_unit` is already-localized display text (e.g. "MJ NE", Danish "FE"),
 * not a stable code, so it never reliably equals a literal 'FU'/'MJ'. This fallback branch is rarely
 * hit (only when the site has no `energyPerWeightUnitDisplayText` configured), but a comparison that
 * can silently never match its MJ NE case is still a real, if dormant, mislabeling bug.
 */
export function energyHeading(t: (key: 'energyUnit' | 'mj' | 'fu') => string): string {
    const energyDisplayText = store.getters.energyPerWeightUnitDisplayText;

    if (energyDisplayText) {
        return t('energyUnit').replace('%VALUE%', energyDisplayText);
    }

    return store.state.user?.settings.energy.id === EnergyUnitId.MjNe ? t('mj') : t('fu');
}

/**
 * The Price column's heading text, unit included - same site-configurable-display-text rule as
 * `energyHeading`. Unlike energy's two-way FU/MJ choice, currency has no small fixed set to fall back
 * to as static per-locale text - a locale string can only ever guess one currency (the ~kr./€/zł
 * literals this codebase already has scattered through it are exactly that: a guess, wrong for any
 * directory not using the guessed currency). So the fallback here reuses the top-level `user.currency`
 * field instead - already the server's own localized currency name for the current directory, no
 * client-side guessing. `t('price')` (no unit at all) is the true last resort, for the rare case a
 * user has no directory to read a currency from at all.
 */
export function priceHeading(t: (key: 'priceUnit' | 'price') => string): string {
    const currencyPerWeightUnit = store.getters.priceCurrencyPerWeightUnitDisplayText || (store.state.user?.currency ? `${store.state.user.currency}/100 kg` : null);

    return currencyPerWeightUnit ? t('priceUnit').replace('%VALUE%', currencyPerWeightUnit) : t('price');
}
